import { useEffect, useState } from 'react'
import { useConversation } from './hooks/useConversation'
import { useAgentStatusPolling } from './hooks/useAgentStatusPolling'
import { ConnectionBadge } from './components/ConnectionBadge'
import { DialogHistory } from './components/DialogHistory'
import { TextInputBar } from './components/TextInputBar'
import { AccessGate } from './components/AccessGate'
import { ModeSelector } from './components/ModeSelector'
import { Settings } from './components/Settings'
import { fetchAuthRequired, getAccessKey } from './net/access'
import { getMeetingAgent, getMode, resolveAgent } from './net/mode'

export default function App() {
  const { state, toggleCall, sendText, stop, setAgentStatus, toggleVoiceOutput, dismissError, reloadHistory } =
    useConversation()

  // REST-polling статуса для реального бэкенда (в моке — no-op).
  useAgentStatusPolling(setAgentStatus)

  // Разовая авторизация: 'checking' → 'locked' (нужна фраза) | 'open'.
  const [gate, setGate] = useState<'checking' | 'locked' | 'open'>('checking')
  // Текущий агент для аватара в поле ввода (обновляется при смене режима).
  const [inputAgent, setInputAgent] = useState(() => resolveAgent(getMode(), getMeetingAgent()))
  const refreshInputAgent = () => setInputAgent(resolveAgent(getMode(), getMeetingAgent()))
  const [showSettings, setShowSettings] = useState(false)
  useEffect(() => {
    // Если фраза уже сохранена — открываем сразу, БЕЗ сетевой проверки.
    // Иначе моргнувшая сеть на старте ложно показала бы экран входа.
    // Если ключ реально устарел — настоящий 401 во время работы вернёт гейт (см. ниже).
    if (getAccessKey()) {
      setGate('open')
      return
    }
    let cancelled = false
    ;(async () => {
      const required = await fetchAuthRequired()
      if (!cancelled) setGate(required ? 'locked' : 'open')
    })()
    return () => {
      cancelled = true
    }
  }, [])

  // Сервер отверг ключ (401) — вернуть экран входа (ключ уже очищен в BridgeSession).
  useEffect(() => {
    const relock = () => setGate('locked')
    window.addEventListener('clawapp:locked', relock)
    return () => window.removeEventListener('clawapp:locked', relock)
  }, [])

  // Когда доступ открыт — подгружаем историю чата с сервера (синхрон между устройствами).
  useEffect(() => {
    if (gate === 'open') void reloadHistory()
  }, [gate, reloadHistory])

  if (gate === 'checking') return <div className="splash" />
  if (gate === 'locked') return <AccessGate onUnlock={() => setGate('open')} />

  const {
    connection,
    callActive,
    micLevel,
    liveUserText,
    liveAgentText,
    history,
    voiceOutput,
    ttsSupported,
    busy,
    thinking,
    currentAgent,
    recState,
    error,
  } = state

  const recording = recState === 'recording' || callActive
  const transcribing = recState === 'transcribing'

  return (
    <div className="app">
      <header className="topbar">
        <div className="topbar__row">
          <ConnectionBadge state={connection} />
          <div className="topbar__actions">
            <VoiceToggle on={voiceOutput} supported={ttsSupported} onToggle={toggleVoiceOutput} />
            <button className="gear" onClick={() => setShowSettings(true)} aria-label="Настройки">⚙</button>
          </div>
        </div>
        <ModeSelector onChange={refreshInputAgent} />
      </header>

      <main className="stage">
        <DialogHistory
          history={history}
          liveUserText={liveUserText}
          liveAgentText={liveAgentText}
          thinking={thinking}
          currentAgent={currentAgent}
        />
      </main>

      <footer className="bottombar">
        {(transcribing || recState === 'recording' || (recording && !transcribing) || busy) && (
          <div className="barhint">
            {transcribing ? (
              <span className="barhint__proc"><span className="spinner" /> Распознаю речь…</span>
            ) : recState === 'recording' ? (
              'Слушаю… нажми микрофон, чтобы отправить'
            ) : recording ? (
              'Идёт запись — нажми микрофон, чтобы остановить'
            ) : (
              'Агент отвечает…'
            )}
          </div>
        )}
        <TextInputBar
          onSend={sendText}
          onStop={stop}
          onMic={toggleCall}
          busy={busy}
          recording={recording}
          transcribing={transcribing}
          micLevel={micLevel}
          agent={inputAgent}
        />
      </footer>

      {showSettings && <Settings onClose={() => setShowSettings(false)} />}

      {error && (
        <div className="toast" role="alert" onClick={dismissError}>
          <span>{error}</span>
          <button className="toast__close" aria-label="Закрыть">✕</button>
        </div>
      )}
    </div>
  )
}

function VoiceToggle({
  on,
  supported,
  onToggle,
}: {
  on: boolean
  supported: boolean
  onToggle: () => void
}) {
  if (!supported) {
    return <span className="voice-toggle voice-toggle--off" title="Синтез речи не поддерживается">🔇</span>
  }
  return (
    <button
      type="button"
      className={`voice-toggle ${on ? 'voice-toggle--on' : 'voice-toggle--muted'}`}
      onClick={onToggle}
      aria-pressed={on}
      aria-label={on ? 'Выключить озвучку ответов' : 'Включить озвучку ответов'}
      title={on ? 'Голос агента включён' : 'Голос агента выключен'}
    >
      {on ? '🔊' : '🔈'}
      <span className="voice-toggle__label">{on ? 'Голос вкл' : 'Голос выкл'}</span>
    </button>
  )
}
